// Interfaces
import { ISharedPreconditionFormTableProps } from './interfaces';

// Mobx
import { observer } from 'mobx-react';

// i18n
import { useTranslation } from 'react-i18next';

// Interfaces
import { ISharedPreconditionForm } from 'interfaces/shared';

// Antd
import { ColumnsType } from 'antd/es/table';
import { Button, Tooltip } from 'antd';

// Components
import { BaseTableBody, BaseUploaderInline } from 'components/base';
import { dateUtils_formatDate } from 'utils/common/date.utils';

import { useAssessment } from 'hooks';

const SharedPreconditionFormTable = observer(
  ({ loading, data, onUploadDocument, onDownloadDocument, onDeleteDocument, onPreview }: ISharedPreconditionFormTableProps) => {
    // Hook
    const { t } = useTranslation();

    const { assessment_detail } = useAssessment();

    // Common State
    const columns: ColumnsType<ISharedPreconditionForm> = [
      {
        key: 'name',
        title: t('shared.assessment.assessment.table.name'),
        dataIndex: 'name',
      },
      {
        key: 'description',
        title: t('shared.assessment.assessment.table.description'),
        dataIndex: 'description',
        render: (_, record) => {
          return <div style={{ textAlign: 'left' }}>{record.description}</div>;
        },
      },
      {
        key: 'status',
        title: t('shared.assessment.assessment.table.status'),
        dataIndex: 'status',
        render: (_, record) => {
          return record.uploadFiles.map((el, i) => {
            return (
              <div key={i} style={{ textAlign: 'left' }}>
                {el.status}
              </div>
            );
          });
        },
      },
      {
        key: 'originalFileName',
        title: t('shared.assessment.assessment.table.form'),
        dataIndex: 'originalFileName',
        render: (_, record) => {
          return (
            <div className="flex justify-between">
              <div style={{ maxWidth: '80px' }}>
                <ul style={{ listStyleType: 'initial' }}>
                  {record.uploadFiles.map((el, index) => {
                    return (
                      <div key={index}>
                        {record.isDownloadFromPortal ? (
                          <li onClick={() => onDownloadDocument && onDownloadDocument(el.userFileBlobId!)}>
                            <Tooltip title={t('shared.assessment.assessment.tooltip.downloadFile')}>
                              <a className="text-blue-500 underline">{el.userOriginalFileName}</a>
                            </Tooltip>
                          </li>
                        ) : (
                          <li onClick={() => onDownloadDocument && onDownloadDocument(el.userFileBlobId!)}>
                            <Tooltip title={t('shared.assessment.assessment.tooltip.downloadFile')}>
                              <p className="text-blue-500 underline">{el.userOriginalFileName}</p>
                            </Tooltip>
                          </li>
                        )}
                      </div>
                    );
                  })}
                </ul>
              </div>
              {record.isGeneratefromPortal ? (
                <>
                  {!assessment_detail.isAssesseeSubmitAssessment && record.name === 'FR.IA.05C' ? (
                    <></>
                  ) : (
                    <div className="flex-col my-auto">
                      <Button
                        className={`custom-button common`}
                        type="primary"
                        size="small"
                        onClick={() => onPreview && onPreview(record.name!)}
                        ghost
                      >
                        {t(`app.preview`)}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <></>
              )}
            </div>
          );
        },
      },
      {
        key: 'userOriginalFileName',
        title: t('shared.assessment.assessment.table.signedDocument'),
        dataIndex: 'userOriginalFileName',
        render: (_, record) => {
          return (
            <>
              {record.isFilledbyAssessee ? (
                <BaseUploaderInline
                  originalFileName={record?.TTDAssesse?.userOriginalFileName! || null}
                  onUpload={file => onUploadDocument && onUploadDocument(record.id!, file)}
                  onDelete={() => onDeleteDocument && onDeleteDocument(record.id!)}
                  onDownload={() => onDownloadDocument && onDownloadDocument(record?.TTDAssesse?.userFileBlobId!)}
                  loading={record.loading?.isActionLoading || record.loading?.isDownloadLoading}
                />
              ) : (
                <></>
              )}
            </>
          );
        },
      },
      {
        key: 'date',
        title: t('shared.assessment.assessment.table.date'),
        dataIndex: 'date',
        render: (_, record) => {
          return (
            <div style={{ textAlign: 'left' }}>
              {dateUtils_formatDate(record?.TTDAssesse?.uploadDate || '', { tableFormat: true, withoutTime: true })}
            </div>
          );
        },
      },
    ];

    /**
     * @description Map columns
     *
     * @return {ColumnsType<ISharedPreconditionForm>} ColumnsType<ISharedPreconditionForm>
     */
    const _columns = (): ColumnsType<ISharedPreconditionForm> => {
      return columns.map((column, index) => ({
        ...column,
        width: `${['10', '20', '10', '20', '10', '20'][index]}%`,
      }));
    };

    return (
      <BaseTableBody
        dataSource={[...data]}
        columns={_columns()}
        className="mb-4 table-no-wrap"
        scroll={{ x: 1520 }}
        loading={loading.isLoading}
        isBgPrimaryHeader
      />
    );
  },
);

export { SharedPreconditionFormTable };
